import type {
  FuelAshCategory,
  FuelAshCategoryPayload,
  FuelAshCategoryType,
  fuelAshCategoriesApi,
} from '../../services/fuelAshCategoriesApi';

/**
 * Die komplette Speicher- und Baselinelogik der Aschegehalt-Einstellungsseite als reine
 * Funktionen (33-06) — ohne Browser testbar, weil `vitest.config.ts` nur `environment: 'node'`
 * gegen `src/**\/*.test.ts` fährt (keine Komponenten-Rendertests). Die Oberfläche (33-07) ruft
 * ausschließlich diese Funktionen auf, keine eigene Fehlerpfad-Logik.
 */

/** Primär- oder Sekundärabschnitt der Formularwerte — deckungsgleich mit `FuelAshCategoryType`. */
export type FuelAshSection = FuelAshCategoryType;

/** Der Formularzustand einer Zeile. `startValue`/`minValue`/`maxValue` sind `number | ''`, damit
 *  Mantines `NumberInput` sie direkt binden kann; `''` heißt „leer", nicht 0 (D-03). */
export interface FuelAshRowState {
  key: string;
  id?: number;
  type: FuelAshSection;
  code: string | null;
  name: string;
  startValue: number | '';
  minValue: number | '';
  maxValue: number | '';
  binding: boolean;
  catchAll: boolean;
  nameLocked: boolean;
}

export interface FuelAshFormValues {
  primary: FuelAshRowState[];
  secondary: FuelAshRowState[];
}

/** Wandelt einen DECIMAL-String (oder fehlenden Schlüssel) in `number | ''`. */
function toNumberValue(value: string | null | undefined): number | '' {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? '' : parsed;
}

/** Eine Server-Kategorie in den Zeilenzustand. `key` ist stabil über Speichervorgänge hinweg —
 *  der Vergleichsstand (`loaded`) findet die passende Zeile darüber, nicht über den Listenplatz
 *  (der verschiebt sich nach Einfügen/Entfernen, siehe `saveChangedRows`). */
export function toRowState(category: FuelAshCategory): FuelAshRowState {
  return {
    key: `existing-${category.id}`,
    id: category.id,
    type: category.type,
    code: category.code ?? null,
    name: category.name,
    startValue: toNumberValue(category.startValue),
    minValue: toNumberValue(category.minValue),
    maxValue: toNumberValue(category.maxValue),
    binding: category.binding,
    catchAll: category.catchAll,
    nameLocked: category.nameLocked ?? false,
  };
}

/** Teilt die Serverliste nach `type` — KEINE Sortierung: die Reihenfolge kommt bereits vom
 *  Server (D-11/D-13, `FuelAshCategoryCollectionProvider`). */
export function toFormValues(categories: FuelAshCategory[]): FuelAshFormValues {
  const primary: FuelAshRowState[] = [];
  const secondary: FuelAshRowState[] = [];
  for (const category of categories) {
    const row = toRowState(category);
    if (row.type === 'primary') {
      primary.push(row);
    } else {
      secondary.push(row);
    }
  }
  return { primary, secondary };
}

/** Eine frische, ungespeicherte Sekundärzeile für „Kategorie hinzufügen". */
export function createEmptySecondaryRow(counter: number): FuelAshRowState {
  return {
    key: `new-${counter}`,
    id: undefined,
    type: 'secondary',
    code: null,
    name: '',
    startValue: '',
    minValue: '',
    maxValue: '',
    binding: false,
    catchAll: false,
    nameLocked: false,
  };
}

/** Hat sich diese Zeile gegenüber ihrem geladenen Stand geändert? Entscheidet allein, ob die
 *  Zeile einen eigenen Request braucht — ob das FORMULAR Änderungen hat, beantwortet
 *  `form.isDirty()` (Projektregel, siehe `AnnualWorkingHoursPanel`/`PlantDetail`). */
export function rowChanged(current: FuelAshRowState, loaded: FuelAshRowState): boolean {
  return (
    current.name !== loaded.name ||
    current.startValue !== loaded.startValue ||
    current.minValue !== loaded.minValue ||
    current.maxValue !== loaded.maxValue ||
    current.binding !== loaded.binding
  );
}

/** `''`/nur Leerzeichen → `null`, Zahl → `String(value)`, sonstiger String → getrimmt. Die API
 *  erwartet DECIMAL-Felder als String oder `null`, nie als Zahl oder leeren String (D-03). */
export function toDecimalString(value: number | string): string | null {
  if (typeof value === 'number') {
    return String(value);
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** Primärzeilen senden nie eine Bezeichnung (D-01, D-19) — nur Sekundärzeilen tragen `name`. */
export function toPayload(row: FuelAshRowState): FuelAshCategoryPayload {
  const payload: FuelAshCategoryPayload = {
    startValue: toDecimalString(row.startValue),
    minValue: toDecimalString(row.minValue),
    maxValue: toDecimalString(row.maxValue),
    binding: row.binding,
  };
  if (row.type === 'secondary') {
    payload.name = row.name;
  }
  return payload;
}

/** Schlägt der Speichervorgang einer Zeile fehl, trägt der Fehler ihren Feldpfad
 *  (`<abschnitt>.<index>`) für `prefixViolationPaths` sowie den Originalfehler für
 *  `getApiViolations`/`getApiErrorMessage`. */
export class RowSaveError extends Error {
  failingPath: string;
  cause: unknown;

  constructor(failingPath: string, cause: unknown) {
    super(`Fehler beim Speichern der Zeile ${failingPath}`);
    this.name = 'RowSaveError';
    this.failingPath = failingPath;
    this.cause = cause;
  }
}

export interface RowSavedEvent {
  section: FuelAshSection;
  index: number;
  row: FuelAshRowState;
  saved: FuelAshCategory;
}

/** Nur `create`/`update` werden gebraucht — kein `getAll`/`delete` in der Speicherschleife. */
export type FuelAshCategoryWriteApi = Pick<typeof fuelAshCategoriesApi, 'create' | 'update'>;

const SECTIONS: FuelAshSection[] = ['primary', 'secondary'];

/**
 * Speichert jede geänderte Zeile über einen eigenen API-Aufruf — erst alle Primärzeilen, dann alle
 * Sekundärzeilen, in Listenreihenfolge (D-13). Die geladene Zeile wird über `key` im selben
 * Abschnitt gesucht, nicht über den Index: nach Einfügen oder Entfernen stimmt der Listenplatz
 * nicht mehr (Muster aus `PlantDetail.tsx`).
 *
 * Primärzeilen werden nie angelegt (D-01) — eine Primärzeile ohne `id` wird übersprungen.
 * Sekundärzeilen ohne `id` werden angelegt, mit `id` aktualisiert. Nach jedem erfolgreichen
 * Aufruf feuert `onRowSaved`. Schlägt ein Aufruf fehl, wirft die Funktion sofort `RowSaveError`
 * mit dem Feldpfad der fehlgeschlagenen Zeile — bereits gespeicherte Zeilen bleiben gespeichert.
 */
export async function saveChangedRows(
  values: FuelAshFormValues,
  loaded: FuelAshFormValues,
  api: FuelAshCategoryWriteApi,
  onRowSaved?: (event: RowSavedEvent) => void
): Promise<void> {
  for (const section of SECTIONS) {
    const rows = values[section];
    const loadedRows = loaded[section];
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      const loadedRow = loadedRows.find((candidate) => candidate.key === row.key);
      if (loadedRow && !rowChanged(row, loadedRow)) {
        continue;
      }
      if (section === 'primary' && !row.id) {
        // Primär wird nie angelegt (D-01) — eine neue Primärzeile kann es nicht geben.
        continue;
      }
      try {
        const saved = row.id
          ? await api.update(row.id, toPayload(row))
          : await api.create(toPayload(row));
        onRowSaved?.({ section, index, row, saved });
      } catch (cause) {
        throw new RowSaveError(`${section}.${index}`, cause);
      }
    }
  }
}

/** Entfernt die Zeile mit diesem `key` aus dem Vergleichsstand (nach sofortigem Löschen einer
 *  Sekundärzeile) — Primär bleibt unberührt, weil Primärzeilen keinen Löschknopf tragen (D-01). */
export function removeRowFromBaseline(baseline: FuelAshFormValues, key: string): FuelAshFormValues {
  return {
    primary: baseline.primary.filter((row) => row.key !== key),
    secondary: baseline.secondary.filter((row) => row.key !== key),
  };
}

/** Ersetzt eine Zeile mit gleichem `key` im Vergleichsstand bzw. hängt sie an, wenn sie fehlt —
 *  zieht den Vergleichsstand nach Teilspeichern (ein Aufruf gelingt, ein anderer schlägt fehl)
 *  auf den neuen Serverstand nach. */
export function replaceRowInBaseline(
  baseline: FuelAshFormValues,
  section: FuelAshSection,
  row: FuelAshRowState
): FuelAshFormValues {
  const rows = baseline[section];
  const index = rows.findIndex((candidate) => candidate.key === row.key);
  const nextRows = index === -1 ? [...rows, row] : rows.map((candidate, i) => (i === index ? row : candidate));
  return { ...baseline, [section]: nextRows };
}

export interface FootnoteSummary {
  total: number;
  withoutStartValue: number;
}

/** Zeilen gesamt und gespeicherte Zeilen (mit `id`) ohne Startwert — für den Fußnotentext
 *  „X ohne Startwert" je Tabelle. */
export function footnoteSummary(rows: FuelAshRowState[]): FootnoteSummary {
  return {
    total: rows.length,
    withoutStartValue: rows.filter((row) => row.id !== undefined && row.startValue === '').length,
  };
}

/** Zeilen mit `id` über beide Abschnitte — für den Navigationszähler. */
export function persistedRowCount(values: FuelAshFormValues): number {
  return (
    values.primary.filter((row) => row.id !== undefined).length +
    values.secondary.filter((row) => row.id !== undefined).length
  );
}

/** Bezeichnungen der Kundendatei für zwei Primärcodes, deren Formularname abweicht (Sketch 018,
 *  Hinweis-Tag neben der Zeile). Keine vollständige Übersetzungstabelle — nur die beiden Fälle,
 *  in denen Kundendatei und `PrimaryFuelCatalog`-Code auseinanderfallen. */
export const PRIMARY_SOURCE_HINTS: Partial<Record<string, string>> = {
  heavyOil: 'Heizöl S',
  lightOil: 'Heizöl EL',
};

/** Ob eine Sekundärzeile schon einen eigenen Namen trägt — entscheidet, ob die Lösch-Dialoge den
 *  Namen als Wert einsetzen oder vom Platzhaltersatz „die neue Kategorie" sprechen (F3, Nacharbeit
 *  33-08: der Platzhalter darf nicht wie ein echter Name in Anführungszeichen/Fettdruck stehen). */
export function isUnnamedRow(name: string): boolean {
  return name.trim() === '';
}

/** Erfolgsmeldung nach dem Löschen. Benannte Zeilen bleiben unverändert („Name" gelöscht); die
 *  unbenannte, nie gespeicherte Zeile bekommt einen eigenen Satz ohne Platzhalter in
 *  Anführungszeichen (F3, Nacharbeit 33-08). */
export function deleteSuccessMessage(name: string): string {
  const trimmed = name.trim();
  return isUnnamedRow(name) ? 'Die neue Kategorie wurde gelöscht.' : `„${trimmed}“ gelöscht`;
}
